/**
 * ConnectTrade broker capabilities and plan-tier classification.
 * Capability tags follow our Connect Trade console / product gating:
 *   Pro requires account data + live trading + market data + (equities OR futures).
 *   Essentials requires account data (rules, analytics, overview).
 *
 * @see https://connecttrade.com/integrations
 */

export const MARKET_DATA = {
  YES: 'yes',
  PENDING: 'pending',
  NO: 'no',
};

/** @typedef {'supported' | 'coming-soon' | 'unsupported'} BrokerTierStatus */

/**
 * @param {Pick<ConnectTradeBroker, 'accountData' | 'liveTrading' | 'marketData' | 'equities' | 'futures' | 'ptSupported'>} broker
 */
export function qualifiesForPro(broker) {
  if (broker.ptSupported === false) return false;
  return (
    broker.accountData
    && broker.liveTrading
    && broker.marketData === MARKET_DATA.YES
    && (broker.equities || broker.futures)
  );
}

/** Live brokers with market data rolling out on ConnectTrade (Growth / Builder roadmap). */
export function mayQualifyForProSoon(broker) {
  if (broker.ptSupported === false) return false;
  return (
    broker.accountData
    && broker.liveTrading
    && broker.marketData === MARKET_DATA.PENDING
    && (broker.equities || broker.futures)
  );
}

/**
 * @param {Pick<ConnectTradeBroker, 'accountData' | 'ptSupported'>} broker
 */
export function qualifiesForEssentials(broker) {
  if (broker.ptSupported === false) return false;
  return broker.accountData;
}

/**
 * @param {ConnectTradeBroker} broker
 * @returns {string[]}
 */
export function getBrokerAssetClasses(broker) {
  const classes = [];
  if (broker.equities) classes.push('Equities');
  if (broker.futures) classes.push('Futures');
  return classes;
}

/**
 * @param {ConnectTradeBroker} broker
 * @param {'track' | 'trade' | 'essentials' | 'pro'} tier
 * @param {boolean} [_connectTradeLaunched] - retained for callers; per-broker
 *   `connectTradeLive` is the source of truth for checkmarks vs coming soon
 *   (IBKR, Moomoo, Tradier, …).
 * @returns {BrokerTierStatus}
 */
export function getBrokerTierStatus(broker, tier, _connectTradeLaunched) {
  const proEligible = qualifiesForPro(broker) || mayQualifyForProSoon(broker);
  const eligible = tier === 'trade' || tier === 'pro' ? proEligible : qualifiesForEssentials(broker);
  if (!eligible) return 'unsupported';
  if (broker.connectTradeLive === false) return 'coming-soon';
  return 'supported';
}

/**
 * @typedef {Object} ConnectTradeBroker
 * @property {string} id - slug used in UI / marketing table
 * @property {string} ctCode - Connect Trade API broker code (uppercase)
 * @property {string} name
 * @property {boolean} accountData
 * @property {boolean} liveTrading - live order execution (not paper-only)
 * @property {string} marketData
 * @property {boolean} equities
 * @property {boolean} [options]
 * @property {boolean} futures
 * @property {boolean} connectTradeLive - connectable now via our CT integration
 * @property {boolean} [ptSupported=true] - false = never offer in PT (e.g. TradeZero)
 * @property {string} logoSrc
 * @property {'cover' | 'contain'} [logoFit]
 * @property {'light' | 'dark'} [logoBg]
 * @property {string} [logoAlt]
 */

const icon = (file) => `/images/brokers/${file}`;

/**
 * Capability source of truth for marketing + connect UI.
 * Pro = liveTrading + marketData yes (+ equities/futures).
 * Screenshot / product decisions override public CT marketing when they conflict
 * (e.g. Tradier coming soon → not connectable; Public/Webull no MD → Essentials only).
 *
 * @type {ConnectTradeBroker[]}
 */
export const CONNECT_TRADE_BROKERS = [
  {
    id: 'tradestation',
    ctCode: 'TRADESTATION',
    name: 'TradeStation',
    accountData: true,
    liveTrading: true,
    marketData: MARKET_DATA.YES,
    equities: true,
    options: true,
    futures: true,
    connectTradeLive: true,
    logoSrc: icon('TradeStation.png'),
    logoAlt: 'TradeStation',
  },
  {
    id: 'alpaca',
    ctCode: 'ALPACA',
    name: 'Alpaca',
    accountData: true,
    liveTrading: true,
    marketData: MARKET_DATA.YES,
    equities: true,
    options: true,
    futures: false,
    connectTradeLive: true,
    logoSrc: icon('Alpaca.jpg'),
    logoAlt: 'Alpaca',
  },
  {
    id: 'tastytrade',
    ctCode: 'TASTYTRADE',
    name: 'Tastytrade',
    accountData: true,
    liveTrading: true,
    marketData: MARKET_DATA.YES,
    equities: true,
    options: true,
    futures: true,
    connectTradeLive: true,
    logoSrc: icon('tastytrade.png'),
    logoAlt: 'Tastytrade',
    logoFit: 'contain',
  },
  // Coming soon — paper connect is unreliable; disable until CT/Tradier is solid
  {
    id: 'tradier',
    ctCode: 'TRADIER',
    name: 'Tradier',
    accountData: true,
    liveTrading: true,
    marketData: MARKET_DATA.YES,
    equities: true,
    options: true,
    futures: false,
    connectTradeLive: false,
    logoSrc: icon('Tradier.png'),
    logoAlt: 'Tradier',
  },
  {
    id: 'public',
    ctCode: 'PUBLIC',
    name: 'Public',
    accountData: true,
    liveTrading: true,
    marketData: MARKET_DATA.NO,
    equities: true,
    options: true,
    futures: false,
    connectTradeLive: true,
    logoSrc: icon('Public.png'),
    logoAlt: 'Public',
  },
  {
    id: 'webull',
    ctCode: 'WEBULL',
    name: 'Webull',
    accountData: true,
    liveTrading: true,
    marketData: MARKET_DATA.NO,
    equities: true,
    options: true,
    futures: true,
    connectTradeLive: true,
    logoSrc: icon('Webull.png'),
    logoAlt: 'Webull',
    logoFit: 'contain',
  },
  {
    id: 'lightspeed',
    ctCode: 'LIGHTSPEED',
    name: 'Lightspeed',
    accountData: true,
    liveTrading: true,
    marketData: MARKET_DATA.NO,
    equities: true,
    options: true,
    futures: false,
    connectTradeLive: true,
    logoSrc: icon('Lightspeed.svg'),
    logoAlt: 'Lightspeed',
    logoFit: 'contain',
    logoBg: 'dark',
  },
  {
    id: 'sterling',
    ctCode: 'STERLING',
    name: 'Sterling Trading Tech',
    accountData: true,
    liveTrading: true,
    marketData: MARKET_DATA.NO,
    equities: true,
    options: true,
    futures: false,
    connectTradeLive: true,
    logoSrc: icon('Sterling.png'),
    logoAlt: 'Sterling Trading Tech',
    logoFit: 'contain',
  },
  // Coming soon (Pro + Essentials when live)
  {
    id: 'interactive-brokers',
    ctCode: 'INTERACTIVE_BROKERS',
    name: 'Interactive Brokers',
    accountData: true,
    liveTrading: true,
    marketData: MARKET_DATA.PENDING,
    equities: true,
    options: true,
    futures: true,
    connectTradeLive: false,
    logoSrc: icon('IBKR.png'),
    logoAlt: 'Interactive Brokers',
  },
  {
    id: 'moomoo',
    ctCode: 'MOOMOO',
    name: 'Moomoo',
    accountData: true,
    liveTrading: true,
    marketData: MARKET_DATA.PENDING,
    equities: true,
    options: true,
    futures: false,
    connectTradeLive: false,
    logoSrc: icon('Moomoo.jpg'),
    logoAlt: 'Moomoo',
  },
  // Not supported in PrecisionTrader for now
  {
    id: 'tradezero',
    ctCode: 'TRADEZERO',
    name: 'TradeZero',
    accountData: true,
    liveTrading: true,
    marketData: MARKET_DATA.NO,
    equities: true,
    options: true,
    futures: true,
    connectTradeLive: false,
    ptSupported: false,
    logoSrc: icon('TradeZero.jpg'),
    logoAlt: 'TradeZero',
  },
  // Account aggregation — Essentials only
  {
    id: 'bank-of-america',
    ctCode: 'BANKOFAMERICA',
    name: 'Bank of America',
    accountData: true,
    liveTrading: false,
    marketData: MARKET_DATA.NO,
    equities: true,
    options: true,
    futures: false,
    connectTradeLive: true,
    logoSrc: icon('BankOfAmerica.png'),
    logoAlt: 'Bank of America',
  },
  {
    id: 'capital-one',
    ctCode: 'CAPITALONE',
    name: 'Capital One',
    accountData: true,
    liveTrading: false,
    marketData: MARKET_DATA.NO,
    equities: true,
    options: true,
    futures: false,
    connectTradeLive: true,
    logoSrc: icon('CapitalOne.png'),
    logoAlt: 'Capital One',
  },
  {
    id: 'charles-schwab',
    ctCode: 'SCHWAB',
    name: 'Charles Schwab',
    accountData: true,
    liveTrading: false,
    marketData: MARKET_DATA.NO,
    equities: true,
    options: true,
    futures: false,
    connectTradeLive: true,
    logoSrc: icon('Schwab.png'),
    logoAlt: 'Charles Schwab',
  },
  {
    id: 'chase',
    ctCode: 'CHASE',
    name: 'Chase',
    accountData: true,
    liveTrading: false,
    marketData: MARKET_DATA.NO,
    equities: true,
    options: true,
    futures: false,
    connectTradeLive: true,
    logoSrc: icon('Chase.png'),
    logoAlt: 'Chase',
  },
  {
    id: 'citi',
    ctCode: 'CITI',
    name: 'Citi',
    accountData: true,
    liveTrading: false,
    marketData: MARKET_DATA.NO,
    equities: true,
    options: true,
    futures: false,
    connectTradeLive: true,
    logoSrc: icon('Citi.png'),
    logoAlt: 'Citi',
  },
  {
    id: 'fidelity',
    ctCode: 'FIDELITY',
    name: 'Fidelity',
    accountData: true,
    liveTrading: false,
    marketData: MARKET_DATA.NO,
    equities: true,
    options: true,
    futures: false,
    connectTradeLive: true,
    logoSrc: icon('Fidelity.png'),
    logoAlt: 'Fidelity',
  },
  {
    id: 'pnc',
    ctCode: 'PNC',
    name: 'PNC',
    accountData: true,
    liveTrading: false,
    marketData: MARKET_DATA.NO,
    equities: true,
    options: true,
    futures: false,
    connectTradeLive: true,
    logoSrc: icon('PNC.png'),
    logoAlt: 'PNC',
  },
  {
    id: 'td-bank',
    ctCode: 'TD',
    name: 'TD Bank',
    accountData: true,
    liveTrading: false,
    marketData: MARKET_DATA.NO,
    equities: true,
    options: true,
    futures: false,
    connectTradeLive: true,
    logoSrc: icon('TDBank.png'),
    logoAlt: 'TD Bank',
  },
  {
    id: 'us-bank',
    ctCode: 'USBANK',
    name: 'U.S. Bank',
    accountData: true,
    liveTrading: false,
    marketData: MARKET_DATA.NO,
    equities: true,
    options: true,
    futures: false,
    connectTradeLive: true,
    logoSrc: icon('USBank.png'),
    logoAlt: 'U.S. Bank',
  },
  {
    id: 'wells-fargo',
    ctCode: 'WELLSFARGO',
    name: 'Wells Fargo',
    accountData: true,
    liveTrading: false,
    marketData: MARKET_DATA.NO,
    equities: true,
    options: true,
    futures: false,
    connectTradeLive: true,
    logoSrc: icon('WellsFargo.png'),
    logoAlt: 'Wells Fargo',
  },
];

/** True when the broker can power Pro (live/paper trading + market data, or pending MD). */
export function isProEligible(broker) {
  if (broker.ptSupported === false) return false;
  return (
    qualifiesForPro(broker)
    || mayQualifyForProSoon(broker)
    || isPaperOnlyBroker(broker)
  );
}

/** Connectable in the welcome / connect UI (not coming-soon, not permanently unsupported). */
export function isConnectableBroker(broker) {
  return broker.ptSupported !== false && broker.connectTradeLive === true;
}

/**
 * Connectable now for paper/sim only. Distinct from account-aggregation
 * banks (`liveTrading: false` + no market data).
 */
export function isPaperOnlyBroker(broker) {
  return (
    broker.ptSupported !== false
    && broker.connectTradeLive === true
    && broker.liveTrading === false
    && broker.marketData === MARKET_DATA.YES
  );
}

/**
 * Public broker-table plan badge.
 * Trade-capable brokers (live, then coming soon) sort above Track-only.
 * @returns {{ kind: 'both' | 'track' | 'unsupported', label: string, note?: string, sortRank: number }}
 */
export function getBrokerPlanAvailability(broker) {
  if (broker.ptSupported === false) {
    return { kind: 'unsupported', label: 'Not yet supported', sortRank: 3 };
  }
  const trade = getBrokerTierStatus(broker, 'trade');
  const track = getBrokerTierStatus(broker, 'track');
  if (trade === 'supported') {
    return { kind: 'both', label: 'Trade', sortRank: 0 };
  }
  if (trade === 'coming-soon') {
    return { kind: 'both', label: 'Trade', note: 'Coming soon', sortRank: 1 };
  }
  if (track === 'coming-soon') {
    return { kind: 'track', label: 'Track', note: 'Coming soon', sortRank: 2 };
  }
  if (track === 'supported') {
    return { kind: 'track', label: 'Track', sortRank: 2 };
  }
  return { kind: 'unsupported', label: 'Not yet supported', sortRank: 3 };
}

/**
 * Pricing / brokers page order: live Trade brokers, coming-soon Trade,
 * Track-only, then unsupported. TradeStation stays first inside its group.
 */
export function getConnectTradeBrokersForPlanTable() {
  return [...CONNECT_TRADE_BROKERS].sort((a, b) => {
    const rankA = getBrokerPlanAvailability(a).sortRank;
    const rankB = getBrokerPlanAvailability(b).sortRank;
    if (rankA !== rankB) return rankA - rankB;
    if (a.id === 'tradestation') return -1;
    if (b.id === 'tradestation') return 1;
    return a.name.localeCompare(b.name);
  });
}

/** TradeStation pinned first; remaining brokers alphabetical by name. */
export function getConnectTradeBrokersForDisplay() {
  const tradestation = CONNECT_TRADE_BROKERS.find((b) => b.id === 'tradestation');
  const rest = CONNECT_TRADE_BROKERS
    .filter((b) => b.id !== 'tradestation')
    .sort((a, b) => a.name.localeCompare(b.name));
  return tradestation ? [tradestation, ...rest] : rest;
}

/**
 * Brokers shown in the connect modal for a plan filter.
 * `all` / `track` → account-data brokers; `trade` → Trade-eligible only.
 * Order: TradeStation, other Trade brokers (incl. coming soon / paper-only) A–Z, then rest A–Z.
 *
 * @param {'all' | 'track' | 'trade' | 'essentials' | 'pro'} [planTier='all']
 */
export function getBrokersForConnectPlan(planTier = 'all') {
  const filtered = getConnectTradeBrokersForDisplay().filter((broker) => {
    if (broker.ptSupported === false) return false;
    if (planTier === 'trade' || planTier === 'pro') return isProEligible(broker);
    return qualifiesForEssentials(broker);
  });

  const byName = (a, b) => a.name.localeCompare(b.name);
  const tradestation = filtered.filter((b) => b.id === 'tradestation');
  const pro = filtered
    .filter((b) => b.id !== 'tradestation' && isProEligible(b))
    .sort(byName);
  const rest = filtered
    .filter((b) => b.id !== 'tradestation' && !isProEligible(b))
    .sort(byName);
  return [...tradestation, ...pro, ...rest];
}

/** Pro-tier brokers in display order (TradeStation first, then A–Z). */
export function getProEligibleBrokersForDisplay() {
  return getConnectTradeBrokersForDisplay().filter(isProEligible);
}

/**
 * Homepage "Connect first" strip — honest short list only.
 * Full matrix stays on /brokers via "and more".
 */
export const HOME_START_PATH_BROKER_IDS = [
  'tradestation',
  'alpaca',
  'tastytrade',
  'charles-schwab',
  'public',
  'webull',
];

export function getHomeStartPathBrokersForDisplay() {
  const byId = new Map(CONNECT_TRADE_BROKERS.map((b) => [b.id, b]));
  return HOME_START_PATH_BROKER_IDS.map((id) => byId.get(id)).filter(Boolean);
}

/** Map CT broker codes → display names (for sidebar labels). */
export function getBrokerDisplayNameMap() {
  return Object.fromEntries(
    CONNECT_TRADE_BROKERS.map((b) => [String(b.ctCode).toUpperCase(), b.name])
  );
}

/** Match a connection `broker_name` / CT code to CONNECT_TRADE_BROKERS. */
export function findConnectTradeBroker(brokerName) {
  const key = String(brokerName || '').toUpperCase().replace(/-/g, '_');
  if (!key) return null;
  return (
    CONNECT_TRADE_BROKERS.find(
      (b) =>
        String(b.ctCode || '').toUpperCase().replace(/-/g, '_') === key ||
        String(b.id || '').toUpperCase().replace(/-/g, '_') === key
    ) || null
  );
}
